import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, IconButton, InputAdornment, Stack, TextField, Tooltip, Typography } from '@mui/material';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Draggable, Droppable } from '@hello-pangea/dnd';
import { ts, ts2 } from 'src/utils/translate';
import { calcImageURL } from 'src/utils/url';
import { cmToInch, inchToCm, roundToMax2 } from 'src/utils/imperialConverter';
import { isPlannerMetric } from 'src/components/Planner/planner/planner';
import { useAppDispatch } from 'src/app/hooks';
import { showSnackBar } from 'src/api/ui-slice';
import { FrameState } from 'src/pages/PlannerPage/Planner/dialogs/AdjustFrameDialog/adjustFrameActions';

const HEIGHT_INPUT_DEBOUNCE_MS = 500;

export interface FrameViewProps {
    frame: FrameState;
    devices: any[];
    inputBadReason: string | null;
    onHeightChange: (frameId: string, cm: number) => void;
    onToggleDirection: (frameId: string) => void;
}

export function FrameView({ frame, devices, inputBadReason, onHeightChange, onToggleDirection }: FrameViewProps) {
    const dispatch = useAppDispatch();
    const metric = isPlannerMetric();
    const [heightInput, setHeightInput] = useState<string>(
        '' + (metric ? roundToMax2(frame.height) : roundToMax2(cmToInch(frame.height))),
    );

    useEffect(() => {
        setHeightInput('' + (metric ? roundToMax2(frame.height) : roundToMax2(cmToInch(frame.height))));
    }, [frame.height, metric]);

    const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    const flushHeight = useCallback(
        (val: string) => {
            if (debounceTimer.current) {
                clearTimeout(debounceTimer.current);
                debounceTimer.current = null;
            }
            const num = parseFloat(val);
            if (Number.isFinite(num)) {
                onHeightChange(frame.id, metric ? num : inchToCm(num));
            }
        },
        [metric, onHeightChange, frame.id],
    );

    const handleHeightChange = useCallback((val: string) => {
        setHeightInput(val);
        if (debounceTimer.current) clearTimeout(debounceTimer.current);
        debounceTimer.current = setTimeout(() => flushHeight(val), HEIGHT_INPUT_DEBOUNCE_MS);
    }, [flushHeight]);

    useEffect(() => () => {
        if (debounceTimer.current) clearTimeout(debounceTimer.current);
    }, []);

    const handleToggleDirection = useCallback(
        () => onToggleDirection(frame.id),
        [onToggleDirection, frame.id],
    );

    const tooMuchDevs = devices.length > 5;
    const moreThanOneDev = devices.length > 1;
    const isHorizontal = frame.direction === 'horizontal';

    return (
        <Stack alignItems="center" sx={{ mb: 1.5 }}>
        {tooMuchDevs && (
            <Typography variant="caption" sx={{ color: '#9c0000', mb: 0.5 }}>
                {ts2('frm_err_maxnum', 'Max device count is {MAX}!', { MAX: '5' })}
            </Typography>
        )}
        <Stack
            direction="row"
            alignItems="center"
            justifyContent="center"
            spacing={1}
        >
            <Box sx={{ minWidth: 48, display: 'flex', justifyContent: 'flex-end' }}>
                {moreThanOneDev && (
                    <Box
                        sx={{
                            background: 'white',
                            boxShadow: 1,
                            borderRadius: 1,
                            display: 'flex',
                            alignItems: 'center',
                            p: 0.5,
                        }}
                    >
                        <Tooltip title={ts('frm_tgl_dir', 'Toggle between horizontal and vertical directions')}>
                            <IconButton onClick={handleToggleDirection} size="small">
                                {isHorizontal ? <SwapHorizIcon /> : <SwapVertIcon />}
                            </IconButton>
                        </Tooltip>
                    </Box>
                )}
            </Box>

            <Droppable droppableId={frame.id} direction={isHorizontal ? 'horizontal' : 'vertical'}>
                {(provided) => (
                    <Box
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        sx={{
                            display: 'flex',
                            flexDirection: isHorizontal ? 'row' : 'column',
                            background: 'white',
                            border: '1px solid black',
                            minWidth: 56,
                            minHeight: 56,
                            width: 'fit-content',
                            alignItems: 'center',
                            justifyContent: 'center',
                            p: 0.5,
                            position: 'relative',
                        }}
                    >
                        {devices.length === 0 ? (
                            <Typography
                                onClick={() => dispatch(showSnackBar({
                                    text: ts('frm_info_drag', 'You can drag devices here from the other frame'),
                                    severity: 'info',
                                }))}
                                sx={{
                                    userSelect: 'none',
                                    fontSize: 32,
                                    color: 'limegreen',
                                    textAlign: 'center',
                                    px: 2,
                                    cursor: 'pointer',
                                }}
                            >
                                +
                            </Typography>
                        ) : (
                            devices.map((device, index) => (
                                <Draggable key={device.deviceId} draggableId={device.deviceId} index={index}>
                                    {(prov) => (
                                        <Box
                                            ref={prov.innerRef}
                                            {...prov.draggableProps}
                                            {...prov.dragHandleProps}
                                            sx={{
                                                p: 0.5,
                                                background: 'rgba(255,255,255,0.6)',
                                                width: 60,
                                                height: 60,
                                                position: 'relative',
                                                ...prov.draggableProps.style,
                                            }}
                                            style={prov.draggableProps.style}
                                        >
                                            <Box
                                                component="img"
                                                src={calcImageURL(device.image)}
                                                sx={{ width: 50, height: 50, objectFit: 'contain' }}
                                            />
                                        </Box>
                                    )}
                                </Draggable>
                            ))
                        )}
                        {provided.placeholder}
                    </Box>
                )}
            </Droppable>

            <Box sx={{ minWidth: 140, display: 'flex', justifyContent: 'flex-start' }}>
                {devices.length > 0 && (
                    <Stack
                        direction="row"
                        alignItems="center"
                        spacing={0.5}
                        sx={{
                            background: 'white',
                            boxShadow: 1,
                            borderRadius: 1,
                            p: 0.75,
                        }}
                    >
                        <TextField
                            size="small"
                            value={heightInput}
                            type="number"
                            onChange={(e) => handleHeightChange(e.target.value)}
                            onBlur={(e) => flushHeight(e.target.value)}
                            slotProps={{
                                input: {
                                    endAdornment: (
                                        <InputAdornment position="end">{metric ? 'cm' : 'in'}</InputAdornment>
                                    ),
                                },
                            }}
                            sx={{ width: 110 }}
                        />
                        {inputBadReason && (
                            <Tooltip title={inputBadReason}>
                                <InfoOutlinedIcon sx={{ color: '#e30000' }} />
                            </Tooltip>
                        )}
                    </Stack>
                )}
            </Box>
        </Stack>
        </Stack>
    );
}
